// ── 출퇴근 달력 커스텀 훅 ─────────────────────────────────────────────────────
// AttendanceCalendar 컴포넌트의 모든 상태·효과·메모·핸들러를 담당한다.
// 로더(loadData), 모달 상태, 필터 파생값, 이벤트 핸들러를 모두 반환한다.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { useToast } from '../../../components/Toast';
import {
  CALENDAR_START_YMD,
  getTodayYmd, getWeekSunday, addDays, shiftMonth,
} from './utils';

/**
 * 출퇴근 달력 훅
 * @param {object} [options]
 * @param {string} [options.calendarApiBase] - 달력 API 경로 (기본: '/api/admin/attendance-calendar', 외부회사: '/api/admin/ext/{CODE}/attendance-calendar', 통합 메뉴: '/api/admin/integrated-menus/{id}/attendance-calendar')
 * @param {Array}  [options.selectedCompanyCodes] - 통합 메뉴 회사 칩 선택값 (있으면 모든 fetch 에 ?codes= query 자동 추가)
 * @returns 상태, 파생값, 핸들러 전체
 */
export function useAttendanceCalendar({ calendarApiBase = '/api/admin/attendance-calendar', selectedCompanyCodes = null } = {}) {
  // codes query 직렬화 — 통합 메뉴에서 회사 칩 변경 시 fetch 자동 갱신
  const codesQ = (Array.isArray(selectedCompanyCodes) && selectedCompanyCodes.length > 0)
    ? `&codes=${selectedCompanyCodes.join(',')}` : '';
  const { showToast } = useToast();
  const today = getTodayYmd();

  // ── 뷰 상태 ────────────────────────────────────────────────────────────
  const [year,      setYear]      = useState(Number(today.slice(0, 4)));
  const [month,     setMonth]     = useState(Number(today.slice(5, 7)));
  const [viewMode,  setViewMode]  = useState('month'); // 'month' | 'week'
  const [weekStart, setWeekStart] = useState(getWeekSunday(today));
  const [calData,   setCalData]   = useState(null);
  const [loading,   setLoading]   = useState(false);

  // ── 필터 상태 ──────────────────────────────────────────────────────────
  const [filterDept,   setFilterDept]   = useState('');
  const [filterName,   setFilterName]   = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // ── 모달 상태 ──────────────────────────────────────────────────────────
  /** 날짜 상세 모달: null | day 객체 */
  const [dayDetailModal,       setDayDetailModal]       = useState(null);
  /** 예외 스케줄 모달: null | {} (신규) | { id, ... } (수정) */
  const [overrideModal,        setOverrideModal]        = useState(null);
  /** 출퇴근 수정 모달: null | { cell, date } */
  const [attendanceEditModal,  setAttendanceEditModal]  = useState(null);
  /** 예정 근무지 모달: null | { cell, date } */
  const [plannedLocationModal, setPlannedLocationModal] = useState(null);

  // IP 별칭 목록 (근무지 드롭다운용) — 한 번 로드 후 모든 모달에 공유
  const [ipAliases, setIpAliases] = useState([]);

  // ── IP 별칭 로드 ────────────────────────────────────────────────────────
  // 마운트 시 한 번만 호출, 각 모달에서 개별 fetch하지 않음
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    fetch(`${calendarApiBase}/ip-aliases`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => { if (d.success) setIpAliases(d.aliases || []); })
      .catch(() => {});
  }, [calendarApiBase]);

  // ── 달력 데이터 로드 ────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`${calendarApiBase}?year=${year}&month=${month}${codesQ}`);
      if (!res.ok) {
        showToast(res.data?.message || '달력 로드 실패', 'error');
        return;
      }

      let calendarData = res.data.data;

      // 주별 뷰에서 주가 월 경계를 넘는 경우 인접 달 데이터도 병합
      // 예) 3/29~4/4 주 → 3월 + 4월 데이터를 합쳐 빈 날짜 없이 표시
      if (viewMode === 'week') {
        const weekEnd      = addDays(weekStart, 6);
        const weekEndYear  = Number(weekEnd.slice(0, 4));
        const weekEndMonth = Number(weekEnd.slice(5, 7));
        if (weekEndYear !== year || weekEndMonth !== month) {
          const res2 = await apiClient.get(
            `${calendarApiBase}?year=${weekEndYear}&month=${weekEndMonth}${codesQ}`
          );
          if (res2.ok) {
            const data2  = res2.data.data;
            // employees: id 기준 중복 제거
            const empMap = new Map();
            [...calendarData.employees, ...data2.employees].forEach((e) => empMap.set(e.id, e));
            // days: 날짜가 겹치지 않으므로 단순 병합
            calendarData = {
              ...calendarData,
              employees: Array.from(empMap.values()),
              days:      [...calendarData.days, ...data2.days],
            };
          }
        }
      }

      setCalData(calendarData);
    } catch (err) {
      showToast('네트워크 오류', 'error');
    } finally {
      setLoading(false);
    }
  }, [year, month, viewMode, weekStart, showToast, calendarApiBase, codesQ]);

  // year/month/viewMode/weekStart 변경 시 자동 재로드
  useEffect(() => {
    loadData();
  }, [loadData]);

  // 주별 뷰에서 주가 다른 월에 걸쳐 있으면 자동으로 월 이동
  useEffect(() => {
    if (viewMode !== 'week') return;
    const wsYear  = Number(weekStart.slice(0, 4));
    const wsMonth = Number(weekStart.slice(5, 7));
    if (wsYear !== year || wsMonth !== month) {
      setYear(wsYear);
      setMonth(wsMonth);
    }
  }, [weekStart, viewMode]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ── 날짜 상세 모달 자동 동기화 ─────────────────────────────────────────
  // 출퇴근 수정 후 loadData()가 calData를 갱신하면 열린 일자 모달도 최신 데이터로 교체
  useEffect(() => {
    if (!dayDetailModal || !calData) return;
    const freshDay = calData.days.find((d) => d.date === dayDetailModal.date);
    if (!freshDay) return;
    const empIds = new Set(filteredEmployees.map((e) => e.id));
    const visibleDay = {
      ...freshDay,
      cells: freshDay.cells.filter((c) => {
        if (!empIds.has(c.user_id)) return false;
        if (c.status === 'offday') return false;
        if (filterStatus && c.status !== filterStatus) return false;
        return true;
      }),
    };
    setDayDetailModal(visibleDay);
  // calData가 바뀔 때만 실행 (dayDetailModal 변경은 무한루프 방지를 위해 제외)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calData]);

  // ── 부서 목록 (필터 드롭다운용) ────────────────────────────────────────
  const departments = useMemo(() => {
    const set = new Set((calData?.employees || []).map((e) => e.department).filter(Boolean));
    return Array.from(set).sort();
  }, [calData]);

  // ── 필터된 직원 목록 ────────────────────────────────────────────────────
  const filteredEmployees = useMemo(() => {
    if (!calData) return [];
    return calData.employees.filter((emp) => {
      if (filterDept && emp.department !== filterDept) return false;
      if (filterName && !emp.name.includes(filterName)) return false;
      return true;
    });
  }, [calData, filterDept, filterName]);

  // 필터+상태 기준으로 표시할 셀 키 Set ('YYYY-MM-DD:userId')
  const filteredCells = useMemo(() => {
    const set = new Set();
    const empIds = new Set(filteredEmployees.map((e) => e.id));
    (calData?.days || []).forEach((day) => {
      // 시스템 도입 이전(CALENDAR_START_YMD 이전) 날짜는 표시하지 않음
      if (day.date < CALENDAR_START_YMD) return;
      day.cells.forEach((cell) => {
        if (!empIds.has(cell.user_id)) return;
        // 휴무(offday)·공휴일(holiday)인 직원은 달력에 표시하지 않음 (출근한 경우 offday_work/holiday_work는 표시)
        if (cell.status === 'offday' || cell.status === 'holiday') return;
        if (filterStatus && cell.status !== filterStatus) return;
        set.add(`${day.date}:${cell.user_id}`);
      });
    });
    return set;
  }, [calData, filteredEmployees, filterStatus]);

  // ── 월 이동 ────────────────────────────────────────────────────────────
  function moveMonth(delta) {
    const { year: ny, month: nm } = shiftMonth(year, month, delta);
    setYear(ny);
    setMonth(nm);
    // 주별 뷰에서는 해당 월 1일이 속한 주의 일요일로 weekStart 이동
    if (viewMode === 'week') {
      setWeekStart(getWeekSunday(`${ny}-${String(nm).padStart(2, '0')}-01`));
    }
  }

  // ── 오늘 버튼: year/month/weekStart 를 오늘 기준으로 리셋 ────────────────
  function handleResetToToday() {
    const t = getTodayYmd();
    setYear(Number(t.slice(0, 4)));
    setMonth(Number(t.slice(5, 7)));
    setWeekStart(getWeekSunday(t));
  }

  // ── 엑셀 다운로드 ──────────────────────────────────────────────────────
  async function handleExcelDownload() {
    try {
      const token = localStorage.getItem('accessToken');
      const res   = await fetch(
        `${calendarApiBase}/excel?year=${year}&month=${month}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) { showToast('엑셀 다운로드 실패', 'error'); return; }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `출퇴근달력_${year}_${String(month).padStart(2, '0')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      showToast('엑셀 다운로드 오류');
    }
  }

  // ── 인쇄 ───────────────────────────────────────────────────────────────
  function handlePrint() { window.print(); }

  // ── 예외 스케줄 삭제 ────────────────────────────────────────────────────
  async function handleOverrideDelete(id) {
    // apiClient는 delete가 예약어라 del로 export됨
    const res = await apiClient.del(`${calendarApiBase}/overrides/${id}`);
    if (!res.ok) { showToast(res.data?.message || '삭제 실패', 'error'); return; }
    showToast('예외 스케줄이 삭제되었습니다.');
    setOverrideModal(null);
    loadData();
  }

  // ── 예외 스케줄 저장 ────────────────────────────────────────────────────
  // payloads: 등록 시 배열(날짜별 여러 건), 수정 시 단일 객체
  async function handleOverrideSave(payloads, editId) {
    const list = Array.isArray(payloads) ? payloads : [payloads];
    for (const payload of list) {
      const res = editId
        ? await apiClient.put(`${calendarApiBase}/overrides/${editId}`, payload)
        : await apiClient.post(`${calendarApiBase}/overrides`, payload);
      if (!res.ok) throw new Error(res.data?.message || '저장 실패');
    }
    showToast(editId
      ? '예외 스케줄이 수정되었습니다.'
      : `예외 스케줄 ${list.length}건이 등록되었습니다.`
    );
    loadData();
  }

  // ── 출퇴근 기록 저장 ────────────────────────────────────────────────────
  async function handleAttendanceSave(payload) {
    const res = await apiClient.post(`${calendarApiBase}/attendance`, payload);
    if (!res.ok) throw new Error(res.data?.message || '저장 실패');
    loadData();
  }

  // ── 예정 근무지 저장 ────────────────────────────────────────────────────
  async function handlePlannedLocationSave(payload) {
    const res = await apiClient.post(`${calendarApiBase}/planned-location`, payload);
    if (!res.ok) throw new Error(res.data?.message || '저장 실패');
    showToast('예정 근무지가 저장되었습니다.');
    loadData();
  }

  // ── 날짜 클릭 → 상세 모달 오픈 ─────────────────────────────────────────
  function handleDayClick(day) {
    // 필터 적용된 셀만 표시
    const visibleDay = {
      ...day,
      cells: day.cells.filter((c) => filteredCells.has(`${day.date}:${c.user_id}`)),
    };
    setDayDetailModal(visibleDay);
  }

  // ── 주별 뷰 셀 클릭 → 출퇴근 수정 모달 ────────────────────────────────
  function handleWeekCellClick(cell, ymd) {
    setAttendanceEditModal({ cell, date: ymd });
  }

  return {
    // 뷰 상태
    year, month, viewMode, setViewMode,
    weekStart, setWeekStart,
    calData, loading,
    // 필터 상태
    filterDept,   setFilterDept,
    filterName,   setFilterName,
    filterStatus, setFilterStatus,
    // 파생값
    departments, filteredEmployees, filteredCells,
    // 모달 상태
    dayDetailModal,       setDayDetailModal,
    overrideModal,        setOverrideModal,
    attendanceEditModal,  setAttendanceEditModal,
    plannedLocationModal, setPlannedLocationModal,
    // 기타
    ipAliases,
    // 핸들러
    moveMonth, handleResetToToday,
    handleExcelDownload, handlePrint,
    handleOverrideDelete, handleOverrideSave,
    handleAttendanceSave, handlePlannedLocationSave,
    handleDayClick, handleWeekCellClick,
    loadData,
  };
}
